import type { MacroContentNode } from './preloadMacroData.ts';

const ADF_EXTENSION_NODE_TYPES = new Set([
	'extension',
	'bodiedExtension',
	'inlineExtension',
	'multiBodiedExtension',
]);
const LCM_EXTENSION_TYPE = 'com.atlassian.confluence.migration';
const LCM_EXTENSION_KEY = 'legacy-content';

type FullLcmHierarchyTraversalOptions = {
	documentADF: unknown;
	isPreloadableMacroNode: (node: MacroContentNode) => boolean;
	maxExtensionsToPreload: number;
	maxNodesToIterateOver: number;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isAdfExtensionNode = (node: MacroContentNode): boolean =>
	ADF_EXTENSION_NODE_TYPES.has(node.type);

const isExactLcmNode = (node: MacroContentNode): boolean =>
	isAdfExtensionNode(node) &&
	node.attrs?.extensionType === LCM_EXTENSION_TYPE &&
	node.attrs?.extensionKey === LCM_EXTENSION_KEY;

const getValidNestedContentDocument = (node: MacroContentNode): Record<string, unknown> | null => {
	if (!isAdfExtensionNode(node)) {
		return null;
	}

	const nestedContent = node.attrs?.parameters?.nestedContent;
	return isObject(nestedContent) &&
		nestedContent.type === 'doc' &&
		Array.isArray(nestedContent.content)
		? nestedContent
		: null;
};

const pushChildrenInDocumentOrder = (stack: unknown[], content: unknown[]): void => {
	for (let index = content.length - 1; index >= 0; index--) {
		stack.push(content[index]);
	}
};

/**
 * Collects preloadable nodes from the outer ADF document and from complete `nestedContent`
 * hierarchies rooted at exact LCM nodes, at any depth. Only LCM nodes are followed, matching the
 * renderer, which recurses into `nestedContent` for legacy-content migration extensions and nothing
 * else.
 *
 * Node and extension limits are shared across the outer and nested traversal, so documents are
 * walked breadth-first: the outer document is exhausted before any `nestedContent` hierarchy is
 * entered, and each hierarchy level before the next. Without that ordering a single macro-heavy LCM
 * could consume the whole extension budget and displace the top-level, above-the-fold macros that
 * are preloaded today.
 */
export const getPreloadableMacroNodesWithFullLcmHierarchy = ({
	documentADF,
	isPreloadableMacroNode,
	maxExtensionsToPreload,
	maxNodesToIterateOver,
}: FullLcmHierarchyTraversalOptions): MacroContentNode[] => {
	const matched: MacroContentNode[] = [];
	// Depth-first stack for the document currently being walked.
	const stack: unknown[] = [documentADF];
	// `nestedContent` roots discovered so far, walked only once the current document is exhausted.
	// A cursor rather than `shift()` keeps appending a newly discovered hierarchy O(1).
	const deferredHierarchies: unknown[] = [];
	let nextDeferredIndex = 0;
	let visited = 0;

	const hasBudgetRemaining = (): boolean =>
		(maxNodesToIterateOver === -1 || visited < maxNodesToIterateOver) &&
		(maxExtensionsToPreload === -1 || matched.length < maxExtensionsToPreload);

	while (hasBudgetRemaining()) {
		if (stack.length === 0) {
			if (nextDeferredIndex >= deferredHierarchies.length) {
				break;
			}
			stack.push(deferredHierarchies[nextDeferredIndex++]);
		}

		const candidate = stack.pop();
		if (!isObject(candidate)) {
			continue;
		}
		visited++;

		const node = candidate as unknown as MacroContentNode;
		if (isPreloadableMacroNode(node)) {
			matched.push(node);
		}

		if (Array.isArray(node.content)) {
			pushChildrenInDocumentOrder(stack, node.content);
		}

		// Only LCM nodes carry content the renderer will recurse into, and `isExactLcmNode` reads the
		// ADF `attrs` form, so nested LCMs are recognised on their own at every depth. Following
		// `nestedContent` on any other extension would spend budget on content that is never rendered.
		const nestedContent = isExactLcmNode(node) ? getValidNestedContentDocument(node) : null;
		if (nestedContent) {
			deferredHierarchies.push(nestedContent);
		}
	}

	return matched;
};
